import React from "react";
import { toast } from "sonner";
import { Copy, Mail } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
import { useMediaQuery } from "@/hooks/use-media-query";
import { Separator } from "@/components/ui/separator";
import { isDesktopSize } from "utils/is-desktop";
import { markEmailImportOpened } from "modules/lists/utils/email-import-flag";

/** Email address users must forward their supermarket receipt to. */
export const EMAIL_IMPORT_ADDRESS = "compras@carriapp.cl";

/** Popup title shown in both the desktop dialog and the mobile sheet. */
const POPUP_TITLE = "Importa tu compra anterior";

/** Instruction shown at the top of the popup. */
const POPUP_INSTRUCTION = `Reenvía el correo de confirmación de tu supermercado a ${EMAIL_IMPORT_ADDRESS} y armamos tu lista automáticamente`;

/** The 3 forwarding steps, in order. */
const IMPORT_STEPS: string[] = [
    "Busca el correo de confirmación de tu última compra (Jumbo, Lider, Unimarc, Tottus, Santa Isabel, etc.)",
    "Reenvíalo a compras@carriapp.cl.",
    "Avisamos a tu correo cuando tu lista esté lista (unos minutos)",
];

/** Decorative Mail badge shown next to the popup title. */
const PopupMailBadge: React.FC = () => (
    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-50 text-sky-500">
        <Mail className="h-4 w-4" aria-hidden="true" data-testid="email-import-popup-mail-icon" />
    </span>
);

interface EmailImportPopupBodyProps {
    /** Called when the user dismisses the popup via the "Listo" CTA. */
    onDismiss: () => void;
}

/**
 * Shared popup body: copy-to-clipboard CTA, secondary copy, the 3 forwarding
 * steps and the "Listo" dismiss CTA. Rendered inside a Dialog (desktop) or
 * a bottom Sheet (mobile).
 */
const EmailImportPopupBody: React.FC<EmailImportPopupBodyProps> = ({ onDismiss }) => {
    /**
     * Copies the import address to the clipboard and reports the outcome.
     * Never throws — clipboard failures surface as an error toast.
     */
    const handleCopy = async (): Promise<void> => {
        try {
            await navigator.clipboard.writeText(EMAIL_IMPORT_ADDRESS);
            toast.success("Dirección copiada");
        } catch {
            toast.error("No se pudo copiar la dirección");
        }
    };

    return (
        <div className="flex flex-col gap-5">
            <div className="flex flex-col items-stretch gap-3">
                <Button
                    type="button"
                    variant="outline"
                    aria-label="Copiar dirección de correo"
                    className="w-full border-sky-200 bg-sky-50 hover:bg-sky-100"
                    onClick={() => {
                        void handleCopy();
                    }}
                >
                    <span className="flex-1 text-left font-medium text-sky-700">
                        {EMAIL_IMPORT_ADDRESS}
                    </span>
                    <Copy className="h-4 w-4 text-sky-500" aria-hidden="true" />
                    <span className="text-sm text-sky-700">copiar</span>
                </Button>
                <p className="text-sm text-muted-foreground">
                    Nosotros identificamos los productos y creamos tu lista de intención en Carriapp. Sin digitar nada,
                </p>
            </div>

            <Separator data-testid="email-import-separator" />

            <ol className="flex flex-col gap-3 text-sm text-foreground/90">
                {IMPORT_STEPS.map((step, index) => (
                    <li key={step} className="flex items-start gap-2">
                        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sky-100 text-xs font-semibold text-sky-700">
                            {index + 1}
                        </span>
                        <span>{step}</span>
                    </li>
                ))}
            </ol>

            <Button type="button" size="xl" className="w-full" onClick={onDismiss}>
                Listo
            </Button>
        </div>
    );
};

interface EmailImportDialogProps {
    /** Controlled visibility — the caller owns open state. */
    open: boolean;
    /** Called with the next open state on any close/open path. */
    onOpenChange: (open: boolean) => void;
    /** UUID of the list the popup belongs to — scopes the localStorage flag. */
    listUuid: string;
    /**
     * Optional trigger node rendered as the Radix Trigger (asChild). Used by
     * the EmailImportCard fallback so focus restore and open-on-click keep the
     * pre-extraction semantics; the page-owned path renders no trigger.
     */
    trigger?: React.ReactNode;
}

/**
 * Controlled email-import popup (desktop Dialog / mobile bottom Sheet).
 *
 * The caller owns `open` — both entry points (the empty-state card fallback and
 * the quick-entry menu in ListDetailPage) render this single dialog. On ANY
 * close path (Listo, Escape, close X, overlay), the per-list
 * `markEmailImportOpened` flag is persisted so the "already sent" copy stays
 * consistent across entry points.
 */
const EmailImportDialog: React.FC<EmailImportDialogProps> = ({ open, onOpenChange, listUuid, trigger }) => {
    const isDesktop = useMediaQuery(isDesktopSize);

    /**
     * Wraps the controlled transition: whenever the dialog goes from open to
     * closed, record that the user has seen the popup for this list.
     */
    const handleOpenChange = (nextOpen: boolean): void => {
        if (open && !nextOpen) markEmailImportOpened(listUuid);
        onOpenChange(nextOpen);
    };

    /** Closes the popup from the "Listo" CTA (a close path like any other). */
    const handleDismiss = (): void => {
        handleOpenChange(false);
    };

    const body = <EmailImportPopupBody onDismiss={handleDismiss} />;

    if (isDesktop) {
        return (
            <Dialog open={open} onOpenChange={handleOpenChange}>
                {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
                <DialogContent data-testid="email-import-dialog" className="max-w-md gap-5">
                    <DialogHeader className="text-left">
                        <div
                            data-testid="email-import-popup-title-row"
                            className="flex items-center gap-2 pr-10"
                        >
                            <PopupMailBadge />
                            <DialogTitle>{POPUP_TITLE}</DialogTitle>
                        </div>
                        <DialogDescription>{POPUP_INSTRUCTION}</DialogDescription>
                    </DialogHeader>
                    {body}
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Sheet open={open} onOpenChange={handleOpenChange}>
            {trigger ? <SheetTrigger asChild>{trigger}</SheetTrigger> : null}
            <SheetContent side="bottom" data-testid="email-import-sheet" className="flex max-h-[85dvh] flex-col gap-5 overflow-y-auto rounded-t-3xl">
                <SheetHeader className="text-left">
                    <div
                        data-testid="email-import-popup-title-row"
                        className="flex items-center gap-2 pr-10"
                    >
                        <PopupMailBadge />
                        <SheetTitle>{POPUP_TITLE}</SheetTitle>
                    </div>
                    <SheetDescription>{POPUP_INSTRUCTION}</SheetDescription>
                </SheetHeader>
                {body}
            </SheetContent>
        </Sheet>
    );
};

export { EmailImportDialog };